import { __assign, __awaiter, __extends, __generator } from "tslib";
import Storage from "./Storage";
var MINUTE = 60 * 1000;
/**
 * SessionStorage
 * Extends the behaviour of Storage to manage a session.
 * SessionStorage will add another key to the StorageAdapter which contains Session state
 * (like expire time).
 *
 * When a value is obtained, the validity of the session will be checked before returning the value.
 * When it's not valid, it will reset the Session.
 */
var SessionStorage = /** @class */ (function (_super) {
    __extends(SessionStorage, _super);
    function SessionStorage(config) {
        var _this = _super.call(this, config) || this;
        _this.expire = config.expire || 30 * MINUTE;
        _this.sessionStorageStateKey = config.sessionStorageStateKey + "_" + SessionStorage.SESSION_STATE_KEY;
        return _this;
    }
    SessionStorage.prototype.get = function (key, defValue) {
        if (key === this.sessionStorageStateKey) {
            throw Error("Not allowed key");
        }
        return _super.prototype.get.call(this, key, defValue);
    };
    SessionStorage.prototype.getAll = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.manageSession()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, Promise.reject("Not implemented yet")];
                }
            });
        });
    };
    SessionStorage.prototype.has = function (key) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.manageSession()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, _super.prototype.has.call(this, key)];
                }
            });
        });
    };
    /**
     * getSessionState
     * Obtain some information about the session like
     * startTime and expireTime for example
     */
    SessionStorage.prototype.getSessionState = function () {
        return this.getSession();
    };
    SessionStorage.prototype._get = function (key, defValue, doManageSession) {
        if (doManageSession === void 0) { doManageSession = true; }
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(doManageSession !== false)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.manageSession()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/, _super.prototype._get.call(this, key, defValue)];
                }
            });
        });
    };
    SessionStorage.prototype.manageSession = function (customSession) {
        return __awaiter(this, void 0, void 0, function () {
            var session, _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = customSession;
                        if (_a) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.getSession()];
                    case 1:
                        _a = (_b.sent());
                        _b.label = 2;
                    case 2:
                        session = _a;
                        if (!!this.isSessionValid(session)) return [3 /*break*/, 4];
                        return [4 /*yield*/, this.resetSession()];
                    case 3:
                        _b.sent();
                        _b.label = 4;
                    case 4: return [4 /*yield*/, this.extendSession(session)];
                    case 5:
                        _b.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    SessionStorage.prototype._set = function (key, value, options, doManageSession) {
        if (doManageSession === void 0) { doManageSession = true; }
        return __awaiter(this, void 0, void 0, function () {
            var promise;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(doManageSession !== false)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.manageSession()];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        promise = _super.prototype._set.call(this, key, value, __assign(__assign({}, (options || {})), { expire: null }));
                        return [2 /*return*/, promise];
                }
            });
        });
    };
    SessionStorage.prototype.isExpired = function (value) {
        // overwrite super to return false since expiry will be handled by session instead
        return false;
    };
    /**
     * resetSession
     * Knows how to create new ISessionState and how to store it
     */
    SessionStorage.prototype.resetSession = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.clearAll()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, this._set(this.sessionStorageStateKey, this.createSession(), {}, false)];
                }
            });
        });
    };
    /**
     * extendSession
     * Knows how to extend the given or the current ISessionState.
     * This will also be written to storage.
     */
    SessionStorage.prototype.extendSession = function (customSession) {
        return __awaiter(this, void 0, void 0, function () {
            var session;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        session = customSession;
                        if (!!session) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.getSession()];
                    case 1:
                        session = _a.sent();
                        _a.label = 2;
                    case 2:
                        session.eTime = this.generateSessionTime();
                        return [2 /*return*/, this._set(this.sessionStorageStateKey, session, {}, false)];
                }
            });
        });
    };
    /**
     * getSession
     * Knows how to obtain ISessionState from the storage
     */
    SessionStorage.prototype.getSession = function () {
        return _super.prototype._get.call(this, this.sessionStorageStateKey, null);
    };
    /**
     * isSessionValid
     */
    SessionStorage.prototype.isSessionValid = function (session) {
        return session && "eTime" in session && new Date().getTime() <= session.eTime;
    };
    SessionStorage.prototype.createSession = function () {
        return {
            eTime: this.generateSessionTime(),
            startTime: new Date().toString(),
        };
    };
    SessionStorage.prototype.generateSessionTime = function () {
        return new Date().getTime() + this.expire;
    };
    SessionStorage.SESSION_STATE_KEY = "__SESSION_STORAGE_STATE__";
    return SessionStorage;
}(Storage));
export default SessionStorage;
//# sourceMappingURL=SessionStorage.js.map